import React, { useEffect, useState } from "react";
import { adminApiService } from "../../../service/admin/adminApiService";
import Loader from "../../../component/common/loader";

export default function PricingAlgo() {
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [form, setForm] = useState({
    marginPercent: 20,
    flatBuffer: 0,
    roundingTick: 1,
    minPrice: 0,
    maxPrice: 0,
    mrpMultiple: 2,
    enabled: true,
    // extended fields - matching Excel screenshot
    rmbRate: 14.5,
    moq: 1,
    chinaPriceRmb: 7.5,
    markupPercent: 15,
    chinaShippingFirstMileRmb: 3,
    baseWeightKg: 315,
    extraWeightPercent: 5,
    customDutyPercent: 11,
    customDutyCessPercent: 1,
    internationalFreightInr: 189,
    lastMileDeliveryInr: 60,
    platformFeesPercent: 0,
    gstProductPercent: 12,
    gstCustomDutyPercent: 12,
    gstFreightPercent: 18,
    actualGstPercent: 12,
  });
  const [applyAll, setApplyAll] = useState(false);
  const [message, setMessage] = useState("");
  const [calc, setCalc] = useState(null);

  const roundToTick = (value, tick) => {
    if (!tick || tick <= 0) return Number(value) || 0;
    return Math.round((Number(value) || 0) / tick) * tick;
  };

  const fmt = (v) => {
    const n = Number(v || 0);
    // Always round to nearest integer
    return Math.round(n).toLocaleString("en-IN");
  };
  const inr = (v) => `₹${fmt(v)}`;

  const computeCalc = (cfg) => {
    const r = Number(cfg.rmbRate || 0);
    const chinaPriceRmb = Number(cfg.chinaPriceRmb || 0);
    const markupPercent = Number(cfg.markupPercent || 0);
    const firstMileRmb = Number(cfg.chinaShippingFirstMileRmb || 0);
    const moq = Number(cfg.moq || 1);
    
    // Excel formulas exactly matching Excel file values:
    // D6: China Price * RMB Rate = 7.5 * 14.5 = 108.75
    const d6_priceInInr = chinaPriceRmb * r;
    
    // D7: Price in INR * Markup% = 108.75 * 15% = 16.3125  
    const d7_markupInr = d6_priceInInr * (markupPercent / 100);
    
    // D8: First Mile RMB * RMB Rate = 3 * 14.5 = 43.5
    const d8_firstMileInr = firstMileRmb * r;
    
    // D9: Cost on Importer = 108.75 + 16.3125 + 43.5 = 168.5625
    const d9_costOnImporter = d6_priceInInr + d7_markupInr + d8_firstMileInr;

    const baseWeight = Number(cfg.baseWeightKg || 0);
    // D10: =(C10)/1000 where C10 = (J3+J3*5%)*D3 = (300+15)*1 = 315, so D10 = 0.315
    const d10_chargeableWeight = (baseWeight * (1 + Number(cfg.extraWeightPercent || 0) / 100)) / 1000;

    // D11: Custom Duty = Cost on Importer * 11% = 168.5625 * 11% = 18.541875 ≈ 18.5
    const d11_customDuty = d9_costOnImporter * (Number(cfg.customDutyPercent || 0) / 100);
    
    // Custom Cess = Cost on Importer * 1% = 168.5625 * 1% = 1.685625 ≈ 1.7
    const d11_customCess = d9_costOnImporter * (Number(cfg.customDutyCessPercent || 0) / 100);
    
    // Total Custom Duty + Cess = 18.5 + 1.7 = 20.2
    const d11_totalCustom = d11_customDuty + d11_customCess;
    
    // D12: International Freight = 189 (from Excel)
    const d12_internationalFreight = Number(cfg.internationalFreightInr || 189);
    
    // D13: Last Mile = 60
    const d13_lastMile = Number(cfg.lastMileDeliveryInr || 60);

    // C14: Total Landing Cost = D9 + D11 + D12 + D13 = 168.5625 + 18.541875 + 189 + 60 = 436.104375
    const c14_totalLandingCost = d9_costOnImporter + d11_customDuty + d12_internationalFreight + d13_lastMile;

    const d15_addPlatformFees = (Number(cfg.platformFeesPercent || 0) / 100) * c14_totalLandingCost;
    // D16: GST on Product = (D8+D9)*C16 = (First Mile + Cost on Importer) * 12%
    const d16_gstOnProduct = (Number(cfg.gstProductPercent || 0) / 100) * (d8_firstMileInr + d9_costOnImporter);
    const d17_gstOnCustom = (Number(cfg.gstCustomDutyPercent || 0) / 100) * d11_customDuty;
    // D18: GST on Freight = (D12+D15+D13)*C18 = (International Freight + Platform Fees + Last Mile) * 18%
    const d18_gstOnFreight = (Number(cfg.gstFreightPercent || 0) / 100) * (d12_internationalFreight + d15_addPlatformFees + d13_lastMile);
    const totalGstPaid = d16_gstOnProduct + d17_gstOnCustom + d18_gstOnFreight;

    const finalPriceInCart = c14_totalLandingCost + d15_addPlatformFees + totalGstPaid;

    return {
      rmbRate: r,
      moq,
      chinaPriceRmb,
      priceInInr: d6_priceInInr,
      markupPercent,
      markupInr: d7_markupInr,
      firstMileRmb,
      firstMileInr: d8_firstMileInr,
      costOnImporter: d9_costOnImporter,
      chargeableWeight: d10_chargeableWeight,
      customDutyPercent: cfg.customDutyPercent,
      customDuty: d11_customDuty,
      customDutyCessPercent: cfg.customDutyCessPercent,
      customCess: d11_customCess,
      totalCustom: d11_totalCustom,
      internationalFreight: d12_internationalFreight,
      lastMile: d13_lastMile,
      totalLandingCost: c14_totalLandingCost,
      platformFeesPercent: cfg.platformFeesPercent,
      addPlatformFees: d15_addPlatformFees,
      gstProductPercent: cfg.gstProductPercent,
      gstOnProduct: d16_gstOnProduct,
      gstCustomDutyPercent: cfg.gstCustomDutyPercent,
      gstOnCustom: d17_gstOnCustom,
      gstFreightPercent: cfg.gstFreightPercent,
      gstOnFreight: d18_gstOnFreight,
      totalGstPaid,
      finalPriceInCart,
      actualGstPercent: cfg.actualGstPercent,
    };
  };

  useEffect(() => {
    (async () => {
      try {
        setInitialLoading(true);
        const res = await adminApiService.getPricingAlgo();
        const data = res?.data?.data;
        if (data?.config) {
          setForm((prev) => ({ ...prev, ...data.config }));
        }
        if (data?.calc) {
          setCalc(data.calc);
        }
      } catch (err) {
        setMessage("Failed to load pricing algorithm settings");
      } finally {
        setInitialLoading(false);
      }
    })();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : Number(value) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");
    try {
      setLoading(true);
      const payload = { ...form, applyAll };
      const res = await adminApiService.updatePricingAlgo(payload);
      const data = res?.data?.data;
      setMessage(applyAll ? "Settings saved and applied to all products" : "Settings saved");
      if (data?.calc) setCalc(data.calc);
    } catch (err) {
      setMessage("Failed to update pricing algorithm");
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = () => {
    const localCalc = computeCalc(form);
    setCalc(localCalc);
  };

  useEffect(() => {
    // Live-recompute for inline form display
    setCalc(computeCalc(form));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(form)]);

  return (
    <div className="p-4" style={{ position: "relative", minHeight: 200 }}>
      <Loader value={initialLoading} variant="overlay" text="Loading pricing algorithm..." />
      <h1 className="text-2xl font-semibold mb-4">Pricing Algorithm</h1>
      {message && <div className="mb-3 text-sm">{message}</div>}
      {/* Single layout: spreadsheet form on the right only */}
      <form onSubmit={handleSubmit} className="border rounded overflow-hidden">
          <div className="bg-blue-900 text-white px-4 py-2 font-semibold">PRICE CALCULATOR FOR IMPORTERR</div>
          <div className="grid grid-cols-12 gap-0">
            <div className="col-span-12 bg-green-800 text-white px-4 py-1 text-sm font-semibold">LEGENDS</div>

            <div className="col-span-3 bg-green-700 text-white px-4 py-2 text-sm font-semibold">RMB RATE</div>
            <div className="col-span-3 px-4 py-2 border"><input type="number" name="rmbRate" value={form.rmbRate} onChange={handleChange} className="border rounded p-2 w-full" /></div>
            <div className="col-span-6 px-4 py-2 border text-sm">RMB Rate = {form.rmbRate}</div>

            <div className="col-span-6 bg-gray-100 px-4 py-2">China Price as per MOQ (As per actual/API)</div>
            <div className="col-span-3 px-4 py-2 border"><input type="number" name="chinaPriceRmb" value={form.chinaPriceRmb} onChange={handleChange} className="border rounded p-2 w-full" /></div>
            <div className="col-span-3 px-4 py-2 border text-sm">{form.chinaPriceRmb} × {form.rmbRate} = {inr(calc ? calc.priceInInr : 0)}</div>

            <div className="col-span-6 bg-gray-100 px-4 py-2">Markup</div>
            <div className="col-span-3 px-4 py-2 border"><div className="flex items-center gap-2"><input type="number" name="markupPercent" value={form.markupPercent} onChange={handleChange} className="border rounded p-2 w-full" /><span>%</span></div></div>
            <div className="col-span-3 px-4 py-2 border text-sm">{form.markupPercent}% × {inr(calc ? calc.priceInInr : 0)} = {inr(calc ? calc.markupInr : 0)}</div>

            <div className="col-span-6 bg-gray-100 px-4 py-2">China Shipping/First Mile (As per API)</div>
            <div className="col-span-3 px-4 py-2 border"><input type="number" name="chinaShippingFirstMileRmb" value={form.chinaShippingFirstMileRmb} onChange={handleChange} className="border rounded p-2 w-full" /></div>
            <div className="col-span-3 px-4 py-2 border text-sm">{form.chinaShippingFirstMileRmb} × {form.rmbRate} = {inr(calc ? calc.firstMileInr : 0)}</div>

            <div className="col-span-6 font-semibold px-4 py-2">Cost of Product/Price on Importer</div>
            <div className="col-span-6 px-4 py-2 border font-semibold text-sm">{inr(calc ? calc.priceInInr : 0)} + {inr(calc ? calc.markupInr : 0)} + {inr(calc ? calc.firstMileInr : 0)} = {inr(calc ? calc.costOnImporter : 0)}</div>

            <div className="col-span-6 bg-gray-100 px-4 py-2">Chargeable Weight (5% Extra)</div>
            <div className="col-span-3 px-4 py-2 border"><div className="flex items-center gap-2"><input type="number" name="baseWeightKg" value={form.baseWeightKg} onChange={handleChange} className="border rounded p-2 w-full" /><span>kg</span></div></div>
            <div className="col-span-3 px-4 py-2 border text-sm">{form.baseWeightKg} × (1 + {form.extraWeightPercent}%) = {calc ? calc.chargeableWeight : 0} kg</div>

            <div className="col-span-6 bg-gray-100 px-4 py-2">Custom duty on cost of product + 1% cess</div>
            <div className="col-span-3 px-4 py-2 border"><div className="grid grid-cols-2 gap-2"><div className="flex items-center gap-2"><input type="number" name="customDutyPercent" value={form.customDutyPercent} onChange={handleChange} className="border rounded p-2 w-full" /><span>%</span></div><div className="flex items-center gap-2"><input type="number" name="customDutyCessPercent" value={form.customDutyCessPercent} onChange={handleChange} className="border rounded p-2 w-full" /><span>%</span></div></div></div>
            <div className="col-span-3 px-4 py-2 border text-sm">Duty: {form.customDutyPercent}% × {inr(calc ? calc.costOnImporter : 0)} = {inr(calc ? calc.customDuty : 0)} | Cess: {form.customDutyCessPercent}% × {inr(calc ? calc.costOnImporter : 0)} = {inr(calc ? calc.customCess : 0)}</div>

            <div className="col-span-6 bg-gray-100 px-4 py-2">International Freight</div>
            <div className="col-span-3 px-4 py-2 border"><input type="number" name="internationalFreightInr" value={form.internationalFreightInr} onChange={handleChange} className="border rounded p-2 w-full" /></div>
            <div className="col-span-3 px-4 py-2 border text-sm">Freight = {inr(form.internationalFreightInr)}</div>

            <div className="col-span-6 bg-gray-100 px-4 py-2">Last Mile Delivery</div>
            <div className="col-span-3 px-4 py-2 border"><input type="number" name="lastMileDeliveryInr" value={form.lastMileDeliveryInr} onChange={handleChange} className="border rounded p-2 w-full" /></div>
            <div className="col-span-3 px-4 py-2 border text-sm">Last Mile = {inr(form.lastMileDeliveryInr)}</div>

            <div className="col-span-6 font-semibold px-4 py-2">Total Landing Cost</div>
            <div className="col-span-6 px-4 py-2 border font-semibold text-sm">{inr(calc ? calc.costOnImporter : 0)} + {inr(calc ? calc.customDuty : 0)} + {inr(calc ? calc.customCess : 0)} + {inr(form.internationalFreightInr)} + {inr(form.lastMileDeliveryInr)} = {inr(calc ? calc.totalLandingCost : 0)}</div>

            <div className="col-span-6 bg-gray-100 px-4 py-2">Add: Platform fees</div>
            <div className="col-span-3 px-4 py-2 border"><div className="flex items-center gap-2"><input type="number" name="platformFeesPercent" value={form.platformFeesPercent} onChange={handleChange} className="border rounded p-2 w-full" /><span>%</span></div></div>
            <div className="col-span-3 px-4 py-2 border text-sm">{form.platformFeesPercent}% × {inr(calc ? calc.totalLandingCost : 0)} = {inr(calc ? calc.addPlatformFees : 0)}</div>

            <div className="col-span-6 bg-gray-100 px-4 py-2">GST on Product</div>
            <div className="col-span-3 px-4 py-2 border"><div className="flex items-center gap-2"><input type="number" name="gstProductPercent" value={form.gstProductPercent} onChange={handleChange} className="border rounded p-2 w-full" /><span>%</span></div></div>
            <div className="col-span-3 px-4 py-2 border text-sm">{form.gstProductPercent}% × ({inr(calc ? calc.firstMileInr : 0)} + {inr(calc ? calc.costOnImporter : 0)}) = {inr(calc ? calc.gstOnProduct : 0)}</div>

            <div className="col-span-6 bg-gray-100 px-4 py-2">GST on Custom duty</div>
            <div className="col-span-3 px-4 py-2 border"><div className="flex items-center gap-2"><input type="number" name="gstCustomDutyPercent" value={form.gstCustomDutyPercent} onChange={handleChange} className="border rounded p-2 w-full" /><span>%</span></div></div>
            <div className="col-span-3 px-4 py-2 border text-sm">{form.gstCustomDutyPercent}% × {inr(calc ? calc.customDuty : 0)} = {inr(calc ? calc.gstOnCustom : 0)}</div>

            <div className="col-span-6 bg-gray-100 px-4 py-2">GST on Freight</div>
            <div className="col-span-3 px-4 py-2 border"><div className="flex items-center gap-2"><input type="number" name="gstFreightPercent" value={form.gstFreightPercent} onChange={handleChange} className="border rounded p-2 w-full" /><span>%</span></div></div>
            <div className="col-span-3 px-4 py-2 border text-sm">{form.gstFreightPercent}% × ({inr(calc ? calc.internationalFreight : 0)} + {inr(calc ? calc.addPlatformFees : 0)} + {inr(calc ? calc.lastMile : 0)}) = {inr(calc ? calc.gstOnFreight : 0)}</div>

            <div className="col-span-6 font-semibold px-4 py-2">Total GST Paid</div>
            <div className="col-span-6 px-4 py-2 border font-semibold text-sm">{inr(calc ? calc.gstOnProduct : 0)} + {inr(calc ? calc.gstOnCustom : 0)} + {inr(calc ? calc.gstOnFreight : 0)} = {inr(calc ? calc.totalGstPaid : 0)}</div>

            <div className="col-span-6 font-semibold px-4 py-2">FINAL PRICE IN CART</div>
            <div className="col-span-6 px-4 py-2 border font-semibold text-sm">{inr(calc ? calc.totalLandingCost : 0)} + {inr(calc ? calc.addPlatformFees : 0)} + {inr(calc ? calc.totalGstPaid : 0)} = {inr(calc ? calc.finalPriceInCart : 0)}</div>

            <div className="col-span-6 bg-gray-100 px-4 py-2">Actual GST as per HSN</div>
            <div className="col-span-6 px-4 py-2 border"><div className="flex items-center gap-2"><input type="number" name="actualGstPercent" value={form.actualGstPercent} onChange={handleChange} className="border rounded p-2 w-full" /><span>%</span></div></div>

            <div className="col-span-6 bg-gray-100 px-4 py-2">Per Piece Product Price in China</div>
            <div className="col-span-6 px-4 py-2 border">₹{calc ? calc.costOnImporter : 0}</div>

            <div className="col-span-6 bg-gray-100 px-4 py-2">Per piece Selling Price in India</div>
            <div className="col-span-6 px-4 py-2 border">₹{calc ? calc.finalPriceInCart : 0}</div>
          </div>
            <div className="col-span-2 md:col-span-3 px-4 py-3 border-t">
              <label className="inline-flex items-center gap-2 mr-6">
                <input type="checkbox" name="enabled" checked={!!form.enabled} onChange={(e)=>setForm(prev=>({...prev, enabled: e.target.checked}))} />
                <span>Enabled</span>
              </label>
              <label className="inline-flex items-center gap-2 mr-6">
                <input type="checkbox" checked={applyAll} onChange={(e)=>setApplyAll(e.target.checked)} />
                <span>Apply to all products now</span>
              </label>
              <button type="submit" disabled={loading} className="px-4 py-2 bg-blue-600 text-white rounded">
                {loading ? "Saving..." : "Update Algorithm"}
              </button>
              <button type="button" onClick={handlePreview} className="ml-3 px-4 py-2 bg-gray-700 text-white rounded">
                Recalculate Preview
              </button>
            </div>
      </form>

      
    </div>
  );
}


